import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Injector,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DevicesClass } from '@app/core/classes';
import { BreadcrumbsDataModel } from '@app/core/models/breadcrumbs';
import { RoutingConfig } from '@app/routing/routing.config';
import { DialogService } from 'primeng/dynamicdialog';
import { DialogIncludesTypes } from '@app/core/models/diallog';
import { NotificationService } from '@app/modules/notification-modules/notification.service';
import { InputValidators } from '@app/core/validators/input.validator';
import { PerformersService } from '@app/modules/account-modules/performers.service';
import { BulletinsService } from '@app/modules/bulletins-modules/bulletins.service';
import { CitiesService } from '@app/modules/cities-module/cities.service';
import { FundamentalsService } from '@app/modules/fundamentals-modules/fundamentals.service';
import { CityModel, CitiesModel, GenreModel } from '@app/modules/fundamentals-modules/fundamentals.models';
import { SearchGenreComponent } from '@app/modules/genres-modules/search-genre/search-genre.component';
import { ReplaySubject, Observable, of, timer } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { FileToBase64 } from '@app/utils/converter';
import { URLLinkValidate } from '@app/utils/validator';
import { ICanDeactivateWithUnsaved } from '@app/core/services/guard/guard-unsaved-form.service';
import { IBulletinDetail } from '@app/modules/bulletins-modules/bulletins.model';
import { CreatePerformerDto, PerformerRateDto, SocialPlatform, PerformerType, PerformerProfileDto } from '@app/modules/account-modules/performers.model';
import { PERFORMERS_PREVIEW_STORAGE_KEY } from '@app/modules/performers-modules/client/performers-preview/performers-preview.model';
import { PerformersPreviewService } from '@app/modules/performers-modules/client/performers-preview/performers-preview.service';
import {
  PERFORMER_TYPES,
  SOCIAL_PLATFORMS,
  PLATFORM_ICON_MAP,
  PlatformIconConfig
} from '../performer-form.constants';

const MAX_FILE_MB = 1;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

function optionalPositiveAmount(control: AbstractControl): ValidationErrors | null {
  const raw = control.value;
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }
  const n = Number(raw);
  if (Number.isNaN(n) || n <= 0) {
    return { positiveAmount: true };
  }
  return null;
}

@UntilDestroy()
@Component({
  selector: 'app-account-performers-create',
  templateUrl: 'performers-create.component.html',
  styleUrls: ['performers-create.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PerformersCreateComponent extends DevicesClass implements OnInit, OnDestroy, ICanDeactivateWithUnsaved {
  readonly Routing = RoutingConfig;
  readonly PerformerTypes = PERFORMER_TYPES;
  readonly SocialPlatforms = SOCIAL_PLATFORMS;
  readonly platformIconMap = PLATFORM_ICON_MAP;

  getPlatformIcon(platformValue: number): PlatformIconConfig {
    return this.platformIconMap[platformValue] ?? { type: 'svg', src: 'assets/images/icons/website.svg' };
  }

  readonly PerformerRateOptions = [
    { value: 'per_hour', label: 'За час' },
    { value: 'per_set', label: 'За сет' },
    { value: 'negotiable', label: 'Договорной' }
  ];

  BreadcrumbsData: BreadcrumbsDataModel = [
    { title: 'Личный кабинет', url: '/' + RoutingConfig.Account },
    { title: 'Мои исполнители', url: '/' + RoutingConfig.Account + '/' + RoutingConfig.AccountPerformers },
    { title: 'Создание', url: '/' + RoutingConfig.Account + '/' + RoutingConfig.AccountPerformers + '/' + RoutingConfig.Create, active: true }
  ];

  form: FormGroup;
  globalError: string = null;
  submitLoading = false;
  /** Show field errors after submit or preview attempt. */
  formSubmitted = false;
  /** Show hint if over limit hashtags */
  showHashtagLimitMessage = false;
  slugPreviewUrl = '';

  Cities: CitiesModel = [];
  CitySelected: CityModel = null;

  GenresSelected: GenreModel[] = [];
  GenresSubject = new ReplaySubject<GenreModel[]>(1);

  PrefillBulletins: IBulletinDetail[] = [];
  prefillBulletinId: number = null;

  MainPhotoPreview: string = null;
  /** Main photo key in buffer (after upload). */
  MainPhotoKey: string | null = null;
  GalleryPreviews: string[] = [];
  /** Gallery photo keys in buffer (same order as GalleryPreviews). */
  GalleryKeys: string[] = [];

  fileAcceptString = 'image/png,image/jpeg,image/jpg';

  biographyLimit = 5000;
  bookingDescriptionLimit = 300;

  constructor(
    injector: Injector,
    public cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private inputValidators: InputValidators,
    private performersService: PerformersService,
    private bulletinsService: BulletinsService,
    private citiesService: CitiesService,
    private fundamentalsService: FundamentalsService,
    private notificationService: NotificationService,
    private performersPreviewService: PerformersPreviewService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.buildForm();
    this.applyNameFromQueryParams();
    this.setDefaultCityByIp();
    this.loadCities();
    this.loadPrefillBulletins();
    this.subscribeGenres();
    this.subscribeSlugPreview();
    this.subscribeBiographyLimit();
    this.subscribeBookingDescriptionLimit();
  }

  ngOnDestroy(): void {
    localStorage.removeItem(PERFORMERS_PREVIEW_STORAGE_KEY);
  }

  private subscribeBiographyLimit(): void {
    this.form.get('biography').valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
      this.biographyLimit = value ? 5000 - value.length : 5000;
      this.cdr.markForCheck();
    });
  }

  private subscribeBookingDescriptionLimit(): void {
    this.form.get('bookingDescription').valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
      this.bookingDescriptionLimit = value ? 300 - value.length : 300;
      this.cdr.markForCheck();
    });
  }

  hasUnsavedChanges(): boolean {
    return this.form && this.form.dirty;
  }

  /** Show validation error for performer types (when touched or after form submit). */
  get showPerformerTypesError(): boolean {
    const ctrl = this.form?.get('performerTypes');
    return !!ctrl && ctrl.invalid && (ctrl.touched || this.formSubmitted);
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload($event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges()) {
      $event.preventDefault();
    }
  }

  private buildForm(): void {
    this.form = this.fb.group({
      cityId: [0, [Validators.required, this.inputValidators.checkIdValue]],
      name: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(200)]],
      biography: ['', [Validators.maxLength(5000)]],
      socialLinks: this.fb.array([]),
      slug: ['', [Validators.required, Validators.maxLength(100), this.inputValidators.checkSlugValue.bind(this.inputValidators)], [this.slugUniquenessValidator()]],
      genreIds: [[], [Validators.required, (c) => (c.value && c.value.length >= 1 && c.value.length <= 5 ? null : { genreCount: true })]],
      performerTypes: [[], [Validators.required, (c) => (c.value && c.value.length >= 1 && c.value.length <= 3 ? null : { performerTypeCount: true })]],
      videoLinks: this.fb.array([
        this.fb.control('', this.inputValidators.checkVKVideoValue.bind(this.inputValidators)),
        this.fb.control('', this.inputValidators.checkVKVideoValue.bind(this.inputValidators))
      ]),
      playlistLink: ['', [this.inputValidators.checkVKPlaylistValue.bind(this.inputValidators)]],
      hashtags: [[], this.inputValidators.checkHashtagsValue.bind(this.inputValidators)],
      hashtagsRaw: [''],
      pricePerHour: [null as number | null, optionalPositiveAmount],
      pricePerSet: [null as number | null, optionalPositiveAmount],
      bookingDescription: ['', Validators.maxLength(300)],
      bookingAvailabilityText: ['', Validators.maxLength(100)]
    });
    this.form.setValidators(() => this.bookingRateValidator());
    this.subscribeBookingGroupRevalidate();
  }

  private subscribeBookingGroupRevalidate(): void {
    const bump = () => this.form.updateValueAndValidity({ emitEvent: false });
    ['pricePerHour', 'pricePerSet', 'bookingDescription'].forEach(name => {
      this.form.get(name)?.valueChanges.pipe(untilDestroyed(this)).subscribe(() => bump());
    });
  }

  private bookingRateValidator(): ValidationErrors | null {
    const ph = this.form?.get('pricePerHour')?.value;
    const ps = this.form?.get('pricePerSet')?.value;
    const desc = (this.form?.get('bookingDescription')?.value ?? '').toString().trim();
    const hasPh = ph != null && ph !== '' && !Number.isNaN(Number(ph)) && Number(ph) > 0;
    const hasPs = ps != null && ps !== '' && !Number.isNaN(Number(ps)) && Number(ps) > 0;
    if (hasPh || hasPs || desc.length > 0) return null;
    return { bookingRateRequired: true };
  }

  private applyNameFromQueryParams(): void {
    const name = this.route.snapshot.queryParamMap.get('name');
    if (name != null && name.trim().length > 0) {
      const value = name.trim().slice(0, 200);
      this.form.patchValue({ name: value });
    }
  }

  private setDefaultCityByIp(): void {
    this.citiesService.cityIdByIp().pipe(untilDestroyed(this)).subscribe(
      city => {
        if (city && city.id) {
          this.form.patchValue({ cityId: city.id });
          this.CitySelected = city;
          this.cdr.markForCheck();
        }
      },
      () => {}
    );
  }

  private loadCities(): void {
    const deps = this.fundamentalsService.citiesDependencies;
    if (deps && deps.length) {
      this.Cities = [{ name: 'Выбрать город', id: 0 }, ...deps];
      if (!this.CitySelected && this.form.get('cityId').value) {
        this.CitySelected = this.citiesService.onGetCityByDictionary(this.form.get('cityId').value);
      }
      this.cdr.markForCheck();
    }
  }

  private loadPrefillBulletins(): void {
    this.bulletinsService.bulletinsActive().pipe(untilDestroyed(this)).subscribe(
      list => {
        this.PrefillBulletins = (list || []).filter(b => b.type === 0 || b.type === 'ArtistSearchBand');
        this.cdr.markForCheck();
      },
      () => {}
    );
  }

  private subscribeGenres(): void {
    this.GenresSubject.pipe(untilDestroyed(this)).subscribe(emit => {
      const genres = (emit && (emit as any).genres) ? (emit as any).genres : [];
      this.GenresSelected = Array.isArray(genres) ? genres : [];
      this.form.get('genreIds').setValue(this.GenresSelected.map((g: GenreModel) => g.id));
      this.cdr.markForCheck();
    });
  }

  onSlugInput(value: string): void {
    const normalized = (value || '').toLowerCase().replace(/[^a-z0-9-]/g, '');
    this.form.get('slug').setValue(normalized, { emitEvent: true });
  }

  private subscribeSlugPreview(): void {
    this.form.get('slug').valueChanges.pipe(untilDestroyed(this)).subscribe(val => {
      const s = (val || '').toString().toLowerCase().replace(/[^a-z0-9-]/g, '');
      this.slugPreviewUrl = s ? `muzrus.com/${s}` : '';
      this.cdr.markForCheck();
    });
  }

  private slugUniquenessValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const slug = (control.value || '').toString().toLowerCase().trim();
      if (!slug || slug.length < 3) return of(null);
      return timer(200).pipe(
        switchMap(() => this.performersService.checkSlug(slug)),
        map(res => (res?.isTaken ? { slugTaken: true } : null)),
        catchError(() => of(null))
      );
    };
  }

  get socialLinksArray(): FormArray {
    return this.form.get('socialLinks') as FormArray;
  }

  get videoLinksArray(): FormArray {
    return this.form.get('videoLinks') as FormArray;
  }

  addSocialLink(): void {
    this.socialLinksArray.push(this.fb.group({
      platform: [SocialPlatform.Instagram],
      url: ['', (c) => (c.value && !URLLinkValidate(c.value) ? { urlInvalid: true } : null)]
    }));
  }

  removeSocialLink(i: number): void {
    this.socialLinksArray.removeAt(i);
  }

  trackByIndex(index: number): number {
    return index;
  }

  onCityDropdownChange(): void {
    this.form.get('cityId').setValue(this.CitySelected ? this.CitySelected.id : 0);
    this.cdr.markForCheck();
  }

  openGenreSearch(): void {
    this.dialogService.open(SearchGenreComponent, {
      header: 'Выберите до 5 жанров',
      width: '90%',
      styleClass: 'p-dialog__genre-search',
      data: {
        includeType: DialogIncludesTypes.ComponentTemplate,
        includeData: {
          dataInitial: { genres: this.GenresSelected },
          dataSubject: this.GenresSubject,
          maxSelection: 5
        }
      }
    });
  }

  togglePerformerType(value: number): void {
    const ctrl = this.form.get('performerTypes');
    const arr: number[] = ctrl.value || [];
    const idx = arr.indexOf(value);
    if (idx === -1) {
      if (arr.length >= 3) return;
      ctrl.setValue([...arr, value]);
    } else {
      ctrl.setValue(arr.filter(x => x !== value));
    }
    ctrl.markAsDirty();
    ctrl.markAsTouched();
    this.cdr.markForCheck();
  }

  isPerformerTypeSelected(value: number): boolean {
    return (this.form.get('performerTypes').value || []).indexOf(value) !== -1;
  }

  isPerformerTypeDisabled(value: number): boolean {
    const arr: number[] = this.form.get('performerTypes').value || [];
    return arr.length >= 3 && arr.indexOf(value) === -1;
  }

  onPrefillFromBulletin(): void {
    const id = this.prefillBulletinId;
    if (!id) return;
    const b = this.PrefillBulletins.find(x => x.id === id) as IBulletinDetail;
    if (!b) return;
    const artist = b.artistAboutViewModel;
    const promo = b.performerPromoViewModel;
    if (artist) {
      this.form.patchValue({
        cityId: artist.cityId || this.form.get('cityId').value,
        name: b.performer || this.form.get('name').value,
        biography: artist.aboutUsText || this.form.get('biography').value,
        genreIds: artist.genreIds || []
      });
      this.CitySelected = this.citiesService.onGetCityByDictionary(artist.cityId) || this.CitySelected;
      this.GenresSelected = (artist.genreIds || []).map(gid => this.fundamentalsService.genresDependencies?.find(g => g.id === gid)).filter(Boolean);
    }
    if (promo) {
      this.form.patchValue({
        playlistLink: promo.publicUrl || this.form.get('playlistLink').value
      });
      const videoCtrl = this.form.get('videoLinks');
      if (promo.youTubeUrl && videoCtrl.get([0])) videoCtrl.get([0]).setValue(promo.youTubeUrl);
    }
    this.form.markAsDirty();
    this.cdr.markForCheck();
  }

  onMainPhotoChange(files: FileList): void {
    if (!files?.length) return;
    const file = files[0];
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      this.notificationService.onDialog({
        type: DialogIncludesTypes.PopupErrorTemplate,
        header: 'Ошибка',
        content: 'Допустимые форматы: PNG, JPG, JPEG.'
      });
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      this.notificationService.onDialog({
        type: DialogIncludesTypes.PopupErrorTemplate,
        header: 'Ошибка',
        content: `Размер файла не должен превышать ${MAX_FILE_MB} Мб.`
      });
      return;
    }
    FileToBase64(file).then((dataUrl: unknown) => {
      this.MainPhotoPreview = dataUrl as string;
      this.form.markAsDirty();
      this.cdr.markForCheck();
    });
    this.performersService.uploadPhotoToBuffer(file).pipe(untilDestroyed(this)).subscribe(
      key => {
        this.MainPhotoKey = key || null;
        this.form.markAsDirty();
        this.cdr.markForCheck();
      },
      err => {
        this.MainPhotoPreview = null;
        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupErrorTemplate,
          header: 'Ошибка загрузки',
          content: (err && (err.message || err.error)) || 'Не удалось загрузить фото. Попробуйте ещё раз.'
        });
        this.cdr.markForCheck();
      }
    );
  }

  clearMainPhoto(): void {
    this.MainPhotoPreview = null;
    this.MainPhotoKey = null;
    this.form.markAsDirty();
    this.cdr.markForCheck();
  }

  mainPhotoDragOver = false;

  onMainPhotoDragOver(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    this.mainPhotoDragOver = true;
    this.cdr.markForCheck();
  }

  onMainPhotoDragLeave(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    this.mainPhotoDragOver = false;
    this.cdr.markForCheck();
  }

  onMainPhotoDrop(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    this.mainPhotoDragOver = false;
    const files = e.dataTransfer?.files;
    if (!files?.length) return;
    const accepted = ['image/png', 'image/jpeg', 'image/jpg'];
    let file: File | null = null;
    for (let i = 0; i < files.length; i++) {
      if (accepted.includes(files[i].type)) {
        file = files[i];
        break;
      }
    }
    if (!file) return;
    const dt = new DataTransfer();
    dt.items.add(file);
    this.onMainPhotoChange(dt.files);
    this.cdr.markForCheck();
  }

  onGalleryChange(files: FileList, inputEl?: HTMLInputElement): void {
    if (!files?.length) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    const toAdd: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      if (!allowedTypes.includes(f.type)) continue;
      if (f.size > MAX_FILE_BYTES) continue;
      toAdd.push(f);
      break;
    }
    if (!toAdd.length) {
      const first = files[0];
      if (first && !allowedTypes.includes(first.type)) {
        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupErrorTemplate,
          header: 'Ошибка',
          content: 'Допустимые форматы: PNG, JPG, JPEG.'
        });
      }
      if (inputEl) inputEl.value = '';
      return;
    }
    const start = this.GalleryPreviews.length;
    toAdd.forEach(() => {
      this.GalleryPreviews.push('');
      this.GalleryKeys.push('');
    });
    toAdd.forEach((f, i) => {
      const idx = start + i;
      FileToBase64(f).then((dataUrl: unknown) => {
        this.GalleryPreviews[idx] = (dataUrl as string) || '';
        this.cdr.markForCheck();
      });
      this.performersService.uploadPhotoToBuffer(f).pipe(untilDestroyed(this)).subscribe(
        key => {
          this.GalleryKeys[idx] = key || '';
          this.form.markAsDirty();
          this.cdr.markForCheck();
        },
        err => {
          this.GalleryPreviews.splice(idx, 1);
          this.GalleryKeys.splice(idx, 1);
          this.notificationService.onDialog({
            type: DialogIncludesTypes.PopupErrorTemplate,
            header: 'Ошибка загрузки',
            content: (err && (err.message || err.error)) || 'Не удалось загрузить фото в галерею.'
          });
          this.form.markAsDirty();
          this.cdr.markForCheck();
        }
      );
    });
    this.form.markAsDirty();
    this.cdr.markForCheck();
    if (inputEl) inputEl.value = '';
  }

  removeGalleryItem(i: number): void {
    this.GalleryPreviews.splice(i, 1);
    this.GalleryKeys.splice(i, 1);
    this.form.markAsDirty();
    this.cdr.markForCheck();
  }

  galleryDragOver = false;

  onGalleryDragOver(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    this.galleryDragOver = true;
    this.cdr.markForCheck();
  }

  onGalleryDragLeave(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    this.galleryDragOver = false;
    this.cdr.markForCheck();
  }

  onGalleryDrop(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    this.galleryDragOver = false;
    const files = e.dataTransfer?.files;
    if (!files?.length) return;
    const accepted = ['image/png', 'image/jpeg', 'image/jpg'];
    let file: File | null = null;
    for (let i = 0; i < files.length; i++) {
      if (accepted.includes(files[i].type)) {
        file = files[i];
        break;
      }
    }
    if (!file) return;
    const dt = new DataTransfer();
    dt.items.add(file);
    this.onGalleryChange(dt.files);
    this.cdr.markForCheck();
  }

  private readonly maxHashtags = 10;

  parseHashtagsInput(val: string): { withHash: string[]; withoutHash: string[]; excess: boolean } {
    const tokens = (val || '')
      .split(/\s+/)
      .map(s => s.trim().replace(/^#+/, ''))
      .filter(s => s.length > 0);
    const limited = tokens.slice(0, this.maxHashtags);
    const withHash = limited.map(t => '#' + t);
    const withoutHash = limited;
    return { withHash, withoutHash, excess: tokens.length > this.maxHashtags };
  }

  formatHashtagsForInput(tagsWithHash: string[]): string {
    return tagsWithHash.map(t => (t.startsWith('#') ? t : '#' + t)).join(' ');
  }

  onHashtagsInput(): void {
    const rawControl = this.form.get('hashtagsRaw');
    if (!rawControl) return;
    const parsed = this.parseHashtagsInput(rawControl.value);
    if (parsed.excess) {
      rawControl.setValue(this.formatHashtagsForInput(parsed.withHash), { emitEvent: false });
      this.form.get('hashtags').setValue(parsed.withoutHash);
      this.showHashtagLimitMessage = true;
      this.cdr.markForCheck();
    } else {
      this.showHashtagLimitMessage = false;
      this.cdr.markForCheck();
    }
  }

  onHashtagsBlur(): void {
    const rawControl = this.form.get('hashtagsRaw');
    if (!rawControl) return;
    const { withHash, withoutHash } = this.parseHashtagsInput(rawControl.value);
    const hashtagsControl = this.form.get('hashtags');
    hashtagsControl.setValue(withoutHash);
    hashtagsControl.markAsTouched();
    rawControl.setValue(this.formatHashtagsForInput(withHash), { emitEvent: false });
    this.showHashtagLimitMessage = false;
    this.cdr.markForCheck();
  }

  /** Field order in template for scrolling to first error. */
  private readonly formFieldOrder: (string | { array: string })[] = [
    'cityId',
    'name',
    'slug',
    'biography',
    'genreIds',
    'performerTypes',
    'hashtagsRaw',
    { array: 'socialLinks' },
    { array: 'videoLinks' },
    'playlistLink',
    'pricePerHour',
    'pricePerSet',
    'bookingDescription',
    'bookingAvailabilityText'
  ];

  private scrollToFirstInvalidField(): void {
    const scroll = () => {
      for (const key of this.formFieldOrder) {
        const controlName = typeof key === 'string' ? key : key.array;
        const control = this.form.get(controlName);
        if (control && control.invalid) {
          const el = document.getElementById('performers-create-field-' + controlName);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          return;
        }
      }
      const globalEl = document.querySelector('.performers-create__global-error');
      if (globalEl) {
        (globalEl as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    setTimeout(scroll, 0);
  }

  onPreview(): void {
    this.formSubmitted = true;
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.form.get('performerTypes').updateValueAndValidity();
    if (this.form.invalid) {
      this.scrollToFirstInvalidField();
      this.cdr.markForCheck();
      return;
    }
    const v = this.form.getRawValue();
    const genreIds = (v.genreIds || []) as number[];
    const performerTypes = (v.performerTypes || []) as number[];
    if (!genreIds.length || genreIds.length > 5) {
      this.cdr.markForCheck();
      return;
    }
    if (!performerTypes.length || performerTypes.length > 3) {
      this.cdr.markForCheck();
      return;
    }
    const socialLinks = (v.socialLinks || []).map((item: { platform: number; url: string }) =>
      ({ platform: typeof item.platform === 'number' ? item.platform : SocialPlatform.Instagram, url: item.url })).filter((item: { url: string }) => !!item.url);
    const videoLinks = (v.videoLinks || []).filter(Boolean).map((url: string, i: number) => ({ url, order: i }));
    const hashtagsRaw = (v.hashtags || []) as string[];
    const hashTagsWithHash = hashtagsRaw.map(t => (t.startsWith('#') ? t : '#' + t));

    const profileDto: PerformerProfileDto = {
      name: v.name || '',
      city: v.cityId,
      bio: v.biography || undefined,
      image: this.MainPhotoPreview ? { path: this.MainPhotoPreview, thumbnailPath: this.MainPhotoPreview } : undefined,
      photos: (this.GalleryPreviews || []).map(url => ({ path: url, thumbnailPath: url })),
      genres: genreIds,
      performerTypes,
      videos: videoLinks.map((link, i) => ({ url: link.url, order: link.order })),
      hashTags: hashTagsWithHash,
      musicPlaylistUrl: v.playlistLink?.trim() ?? '',
      socialLinks
    };
    try {
      this.performersPreviewService.setPreviewData(profileDto);
      localStorage.setItem(PERFORMERS_PREVIEW_STORAGE_KEY, JSON.stringify(profileDto));
      const url = `${window.location.origin}/performers-preview`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      this.notificationService.onDialog({
        type: DialogIncludesTypes.PopupErrorTemplate,
        header: 'Ошибка',
        content: 'Не удалось открыть предпросмотр.'
      });
    }
    this.cdr.markForCheck();
  }

  /** Builds performerRate for create: one item with pricePerHour, pricePerSet, description (validation ensures at least one). */
  private buildPerformerRate(v: Record<string, unknown>): PerformerRateDto {
    const ph = v.pricePerHour;
    const ps = v.pricePerSet;
    const desc = (v.bookingDescription ?? '').toString().trim();
    const pricePerHour = ph != null && ph !== '' && !Number.isNaN(Number(ph)) ? Number(ph) : undefined;
    const pricePerSet = ps != null && ps !== '' && !Number.isNaN(Number(ps)) ? Number(ps) : undefined;
    return { pricePerHour, pricePerSet, description: desc };
  }

  onSubmit(): void {
    this.globalError = null;
    this.formSubmitted = true;
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.form.get('performerTypes').updateValueAndValidity();
    if (this.form.invalid) {
      if (this.form.errors?.bookingRateRequired) {
        this.globalError = 'Укажите сумму за час, сумму за сет или описание (договорная).';
      }
      this.scrollToFirstInvalidField();
      this.cdr.markForCheck();
      return;
    }
    const v = this.form.getRawValue();
    const genreIds = (v.genreIds || []) as number[];
    const performerTypes = (v.performerTypes || []) as number[];
    if (!genreIds.length || genreIds.length > 5) {
      this.globalError = 'Выберите от 1 до 5 жанров.';
      this.scrollToFirstInvalidField();
      this.cdr.markForCheck();
      return;
    }
    if (!performerTypes.length || performerTypes.length > 3) {
      this.globalError = 'Выберите от 1 до 3 типов исполнителя.';
      this.scrollToFirstInvalidField();
      this.cdr.markForCheck();
      return;
    }
    this.submitLoading = true;
    this.cdr.markForCheck();

    const socialLinks = (v.socialLinks || []).map((item: { platform: number; url: string }) =>
      ({ platform: typeof item.platform === 'number' ? item.platform : SocialPlatform.Instagram, url: item.url })).filter((item: { url: string }) => !!item.url);
    const videoLinks = (v.videoLinks || []).filter(Boolean).map((url: string, i: number) => ({ url: (url && url.trim()) || '', order: i })).filter(link => !!link.url);

    const payload: CreatePerformerDto = {
      city: v.cityId,
      name: v.name || null,
      bio: v.biography || undefined,
      socialLinks: socialLinks.length ? socialLinks : [],
      photoKey: this.MainPhotoKey || undefined,
      photoKeys: this.GalleryKeys.filter(Boolean).length ? this.GalleryKeys.filter(Boolean) : [],
      slug: v.slug ? String(v.slug).toLowerCase() : null,
      genres: genreIds,
      performerTypes,
      performerRate: this.buildPerformerRate(v),
      videos: videoLinks.length ? videoLinks : [],
      musicPlaylistUrl: v.playlistLink || null,
      hashTags: v.hashtags?.length ? v.hashtags : [],
      bookingAvailableText: v.bookingAvailabilityText || null
    };

    this.performersService.createPerformer(payload).pipe(untilDestroyed(this)).subscribe(
      res => {
        this.submitLoading = false;
        this.cdr.markForCheck();
        if (!res || (!res.slug && !res.id)) {
          this.globalError = 'Не удалось сохранить данные. Попробуйте позже.';
          this.cdr.markForCheck();
          return;
        }
        localStorage.removeItem(PERFORMERS_PREVIEW_STORAGE_KEY);
        this.form.markAsPristine();
        this.notificationService.onDialog({
          type: DialogIncludesTypes.PopupSuccessTemplate,
          header: 'Профиль опубликован',
          content: 'Профиль успешно создан и опубликован.',
          icons: 'pi pi-check'
        });
        const path = '/' + (res.slug || res.id);
        this.router.navigateByUrl(path).catch(() => {});
        this.cdr.markForCheck();
      },
      err => {
        this.submitLoading = false;
        this.globalError = (err && (err.message || err.error)) || 'Не удалось сохранить данные. Попробуйте позже.';
        this.cdr.markForCheck();
      }
    );
  }
}
